import { useEffect, useState } from "react";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  ArrowDown,
  ArrowUp,
  EyeOff,
  Eye,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  emoji: "✨",
  title: "",
  description: "",
  cta_label: "",
  cta_url: "",
  order: 99,
  active: true,
};

export const AdminActivities = ({ token }) => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // id or "new"
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const headers = {
    "Content-Type": "application/json",
    "X-Admin-Password": token,
  };

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/admin/activities`, {
        headers: { "X-Admin-Password": token },
      });
      if (!res.ok) throw new Error("Errore caricamento.");
      setItems(await res.json());
    } catch (e) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNew = () => {
    setDraft({ ...EMPTY, order: (items?.length || 0) + 1 });
    setEditing("new");
  };

  const startEdit = (a) => {
    setDraft({
      emoji: a.emoji || "✨",
      title: a.title || "",
      description: a.description || "",
      cta_label: a.cta_label || "",
      cta_url: a.cta_url || "",
      order: a.order ?? 99,
      active: a.active ?? true,
    });
    setEditing(a.id);
  };

  const cancel = () => {
    setEditing(null);
    setDraft(EMPTY);
  };

  const save = async () => {
    if (!draft.title.trim() || !draft.description.trim() || !draft.emoji.trim()) {
      toast.error("Emoji, titolo e descrizione sono obbligatori.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        emoji: draft.emoji.trim(),
        title: draft.title.trim(),
        description: draft.description.trim(),
        cta_label: draft.cta_label.trim() || null,
        cta_url: draft.cta_url.trim() || null,
        order: parseInt(draft.order, 10) || 0,
        active: !!draft.active,
      };
      const url =
        editing === "new"
          ? `${API}/api/admin/activities`
          : `${API}/api/admin/activities/${editing}`;
      const method = editing === "new" ? "POST" : "PUT";
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Errore salvataggio.");
      }
      toast.success(editing === "new" ? "Attività creata" : "Attività aggiornata");
      cancel();
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa attività?")) return;
    try {
      const res = await fetch(`${API}/api/admin/activities/${id}`, {
        method: "DELETE",
        headers: { "X-Admin-Password": token },
      });
      if (!res.ok) throw new Error("Errore eliminazione.");
      toast.success("Attività eliminata");
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const updateField = async (a, patch) => {
    try {
      const body = {
        emoji: a.emoji,
        title: a.title,
        description: a.description,
        cta_label: a.cta_label || null,
        cta_url: a.cta_url || null,
        order: a.order,
        active: a.active,
        ...patch,
      };
      const res = await fetch(`${API}/api/admin/activities/${a.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Errore aggiornamento.");
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const moveItem = (a, direction) => {
    if (!items) return;
    const sorted = [...items].sort((x, y) => x.order - y.order);
    const idx = sorted.findIndex((s) => s.id === a.id);
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= sorted.length) return;
    const swapWith = sorted[target];
    updateField(a, { order: swapWith.order });
    updateField(swapWith, { order: a.order });
  };

  return (
    <section
      data-testid="section-activities"
      className="bg-white border border-[#E5E1D8] rounded-xl shadow-sm overflow-hidden"
    >
      <header className="flex items-center justify-between px-5 py-4 border-b border-[#E5E1D8] bg-[#FBF9F6]">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#2E4F3B]" />
          <h2 className="text-[#1a2620] font-semibold">
            Cosa stiamo facendo
          </h2>
          <span className="text-[#5C5C5C] text-sm tabular-nums">
            ({items?.length ?? "…"})
          </span>
        </div>
        <button
          type="button"
          onClick={startNew}
          disabled={editing !== null}
          data-testid="activities-new-btn"
          className="inline-flex items-center gap-2 bg-[#2E4F3B] hover:bg-[#1f3828] disabled:opacity-50 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nuova attività
        </button>
      </header>

      <div className="p-5">
        {/* Editor */}
        {editing && (
          <div
            data-testid="activities-editor"
            className="mb-5 bg-[#FBF9F6] border-2 border-[#2E4F3B] rounded-xl p-5"
          >
            <div className="grid sm:grid-cols-12 gap-3 mb-3">
              <div className="sm:col-span-2">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#5C5C5C] mb-1">
                  Emoji
                </label>
                <input
                  value={draft.emoji}
                  onChange={(e) => setDraft({ ...draft, emoji: e.target.value })}
                  className="w-full bg-white border-2 border-[#E5E1D8] rounded-lg px-3 py-2 text-2xl text-center"
                  data-testid="activities-edit-emoji"
                />
              </div>
              <div className="sm:col-span-8">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#5C5C5C] mb-1">
                  Titolo *
                </label>
                <input
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className="w-full bg-white border-2 border-[#E5E1D8] rounded-lg px-3 py-2"
                  data-testid="activities-edit-title"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#5C5C5C] mb-1">
                  Ordine
                </label>
                <input
                  type="number"
                  value={draft.order}
                  onChange={(e) => setDraft({ ...draft, order: e.target.value })}
                  className="w-full bg-white border-2 border-[#E5E1D8] rounded-lg px-3 py-2"
                  data-testid="activities-edit-order"
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-[#5C5C5C] mb-1">
                Descrizione *
              </label>
              <textarea
                rows={3}
                value={draft.description}
                onChange={(e) =>
                  setDraft({ ...draft, description: e.target.value })
                }
                className="w-full bg-white border-2 border-[#E5E1D8] rounded-lg px-3 py-2 resize-y"
                data-testid="activities-edit-description"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#5C5C5C] mb-1">
                  Etichetta pulsante (facolt.)
                </label>
                <input
                  value={draft.cta_label}
                  onChange={(e) =>
                    setDraft({ ...draft, cta_label: e.target.value })
                  }
                  placeholder="Es. Vota Monte Faeta"
                  className="w-full bg-white border-2 border-[#E5E1D8] rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#5C5C5C] mb-1">
                  Link pulsante (facolt.)
                </label>
                <input
                  value={draft.cta_url}
                  onChange={(e) =>
                    setDraft({ ...draft, cta_url: e.target.value })
                  }
                  placeholder="https://… oppure #volontari"
                  className="w-full bg-white border-2 border-[#E5E1D8] rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]">
                <input
                  type="checkbox"
                  checked={draft.active}
                  onChange={(e) =>
                    setDraft({ ...draft, active: e.target.checked })
                  }
                  className="w-4 h-4"
                />
                Visibile sul sito
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={cancel}
                  className="inline-flex items-center gap-2 bg-white border-2 border-[#E5E1D8] hover:border-[#1A1A1A] text-[#1A1A1A] font-semibold text-sm px-4 py-2 rounded-lg"
                >
                  <X className="w-4 h-4" />
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  data-testid="activities-save-btn"
                  className="inline-flex items-center gap-2 bg-[#2E4F3B] hover:bg-[#1f3828] disabled:opacity-60 text-white font-semibold text-sm px-4 py-2 rounded-lg"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Salva
                </button>
              </div>
            </div>
          </div>
        )}

        {/* List */}
        {items === null ? (
          <div className="flex items-center justify-center py-10 text-[#5C5C5C]">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Caricamento…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-[#5C5C5C] italic py-8">
            Nessuna attività. Aggiungine una.
          </div>
        ) : (
          <ul className="divide-y divide-[#E5E1D8]">
            {[...items].sort((a, b) => a.order - b.order).map((a) => (
              <li
                key={a.id}
                data-testid={`activity-row-${a.id}`}
                className={`flex items-start gap-4 py-4 ${
                  !a.active ? "opacity-50" : ""
                }`}
              >
                <div className="text-3xl select-none w-10 shrink-0">
                  {a.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-[#1A1A1A]">{a.title}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#5C5C5C] bg-[#FBF9F6] px-2 py-0.5 rounded">
                      ord. {a.order}
                    </span>
                    {!a.active && (
                      <span className="text-[10px] uppercase tracking-wider text-[#C15C3D] bg-[#C15C3D]/10 px-2 py-0.5 rounded">
                        Nascosta
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#5C5C5C] mt-1 line-clamp-2">
                    {a.description}
                  </p>
                  {a.cta_label && (
                    <div className="text-xs text-[#2E4F3B] mt-1 font-semibold">
                      → {a.cta_label}{a.cta_url ? ` (${a.cta_url})` : ""}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <div className="flex gap-1">
                    <button
                      onClick={() => moveItem(a, "up")}
                      title="Sposta su"
                      className="w-8 h-8 rounded-md hover:bg-[#FBF9F6] flex items-center justify-center"
                    >
                      <ArrowUp className="w-4 h-4 text-[#5C5C5C]" />
                    </button>
                    <button
                      onClick={() => moveItem(a, "down")}
                      title="Sposta giù"
                      className="w-8 h-8 rounded-md hover:bg-[#FBF9F6] flex items-center justify-center"
                    >
                      <ArrowDown className="w-4 h-4 text-[#5C5C5C]" />
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => updateField(a, { active: !a.active })}
                      title={a.active ? "Nascondi" : "Mostra"}
                      className="w-8 h-8 rounded-md hover:bg-[#FBF9F6] flex items-center justify-center"
                    >
                      {a.active ? (
                        <Eye className="w-4 h-4 text-[#5C5C5C]" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-[#5C5C5C]" />
                      )}
                    </button>
                    <button
                      onClick={() => startEdit(a)}
                      title="Modifica"
                      data-testid={`activity-edit-${a.id}`}
                      className="w-8 h-8 rounded-md hover:bg-[#2E4F3B]/10 flex items-center justify-center"
                    >
                      <Pencil className="w-4 h-4 text-[#2E4F3B]" />
                    </button>
                    <button
                      onClick={() => remove(a.id)}
                      title="Elimina"
                      className="w-8 h-8 rounded-md hover:bg-[#C15C3D]/10 flex items-center justify-center"
                    >
                      <Trash2 className="w-4 h-4 text-[#C15C3D]" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};
